import { StringIndexable } from 'core/utilities/interface-helpers';
import SkeletonElement from 'shared/components/layout/skeleton-element';
import { ISharedTableColumn } from '../table-interface';
import { ISharedTableBody } from './table-body-interface';
import { useEffect, useRef } from 'react';
import {
  draggable as setDraggable,
  dropTargetForElements as setDropTarget,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

const SharedTableBody = <T extends StringIndexable>({
  columns,
  rows,
  templates = [],
  isLoading,
  onRowClick,
  draggable,
  onReorder,
}: ISharedTableBody<T>) => {
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  const findTemplate = (rowData: T, column: ISharedTableColumn) => {
    const cellTemplate = templates.find((template) => template.id === column.templateId);
    return cellTemplate?.template(
      column.templateId === 'text' || column.templateId === 'boolean' ? rowData[column.key] ?? 'Unknown' : rowData
    );
  };

  useEffect(() => {
    if (!draggable || isLoading || !onReorder) {
      return;
    }

    const cleanupFunctions: (() => void)[] = [];

    rowRefs.current.forEach((rowElement, index) => {
      if (!rowElement) return;

      // Make the row draggable
      const draggableCleanup = setDraggable({
        element: rowElement,
        getInitialData: () => ({ index, rowKey: rows[index].key }),
        onDragStart: () => {
          rowElement.style.opacity = '0.5';
        },
        onDrop: () => {
          rowElement.style.opacity = '1';
        },
      });
      cleanupFunctions.push(draggableCleanup);

      // Make the row a drop target
      const dropTargetCleanup = setDropTarget({
        element: rowElement,
        getData: () => ({ index }),
        onDragEnter: () => {
          rowElement.style.backgroundColor = 'rgb(243 244 246)'; // gray-100
        },
        onDragLeave: () => {
          rowElement.style.backgroundColor = '';
        },
        onDrop: ({ source }) => {
          rowElement.style.backgroundColor = '';
          const sourceData = source.data as { index: number };
          if (sourceData.index !== undefined && sourceData.index !== index) {
            onReorder(sourceData.index, index);
          }
        },
      });
      cleanupFunctions.push(dropTargetCleanup);
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [draggable, isLoading, rows, onReorder]);

  return (
    <tbody>
      {isLoading ? (
        <tr>
          {columns.map((column) => (
            <td key={column.key} className='body-02 p-3 border-t'>
              <SkeletonElement width='50%' height='1.25rem' />
            </td>
          ))}
        </tr>
      ) : (
        <>
          {rows.map((row, index) => (
            <tr
              key={row.key}
              ref={(el) => (rowRefs.current[index] = el)}
              className={`hover:bg-gray-50 ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
            >
              {columns.map((column) => (
                <td key={column.key} className={`body-02 p-3 border-t ${onRowClick && 'cursor-pointer'}`}>
                  {findTemplate(row.data, column)}
                </td>
              ))}
            </tr>
          ))}
        </>
      )}
    </tbody>
  );
};

export default SharedTableBody;
